import { cn } from "@/lib/utils";
import { ArrowUpRight } from "lucide-react";

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between animate-in fade-in slide-in-from-bottom-2 duration-500">
    <div>
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{title}</h1>
      {subtitle && <p className="mt-1.5 text-sm text-slate-500">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
  </div>
);

export const Card = ({ className, children, ...rest }) => (
  <div className={cn("rounded-2xl border border-slate-200 bg-white shadow-sm", className)} {...rest}>{children}</div>
);

export const KpiCard = ({ label, value, hint, icon: Icon, tone = "slate", onClick, testId, delay = 0 }) => {
  const tones = {
    slate: "bg-slate-900 text-white", red: "bg-red-600 text-white", emerald: "bg-emerald-600 text-white",
    amber: "bg-amber-500 text-white", indigo: "bg-indigo-600 text-white", sky: "bg-sky-600 text-white",
  };
  return (
    <button type="button" onClick={onClick} data-testid={testId} style={{ animationDelay: `${delay}ms` }}
      className={cn("group relative w-full overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md animate-in fade-in slide-in-from-bottom-3 fill-mode-both", onClick ? "cursor-pointer" : "cursor-default")}>
      <div className="flex items-start justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        {Icon && <span className={cn("rounded-xl p-2", tones[tone])}><Icon className="h-4 w-4" /></span>}
      </div>
      <p className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900 tabular-nums">{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
      {onClick && <ArrowUpRight className="absolute bottom-4 right-4 h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-slate-500" />}
    </button>
  );
};

export const Table = ({ columns, rows, keyFn, empty = "No records", testId, onRowClick }) => (
  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid={testId}>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            {columns.map((c) => <th key={c.key} className={cn("px-4 py-3", c.className)}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-12 text-center text-slate-400">{empty}</td></tr>}
          {rows.map((r, i) => (
            <tr key={keyFn ? keyFn(r) : i} onClick={onRowClick ? () => onRowClick(r) : undefined}
              className={cn("border-b border-slate-100 text-slate-700 transition-colors last:border-0 hover:bg-slate-50/80", onRowClick && "cursor-pointer")}>
              {columns.map((c) => <td key={c.key} className={cn("px-4 py-3 align-middle", c.className)}>{c.render ? c.render(r) : r[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const Select = ({ value, onChange, options, label, testId, className }) => (
  <label className={cn("flex flex-col gap-1 text-xs font-medium text-slate-500", className)}>
    {label}
    <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-800 shadow-sm outline-none transition-colors focus:border-red-500 focus:ring-2 focus:ring-red-100">
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </label>
);

export const Spinner = () => (
  <div className="flex items-center justify-center py-20" data-testid="loading-spinner">
    <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-slate-200 border-t-red-600" />
  </div>
);

export const EmptyState = ({ title, hint }) => (
  <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-16 text-center">
    <p className="font-semibold text-slate-700">{title}</p>
    {hint && <p className="mt-1 text-sm text-slate-500">{hint}</p>}
  </div>
);
